<template lang="pug">
include /mixins

container(space="md", align="left")
  row(appearance="spaced")
    //- секция менеджеров. видна только админу
    cell(
      v-if="shouldShowManagersSection"
      :cols="['12', '2-xl']"
      class="tg-unidentified-cell"
    )
      form-fieldset(:caption="_('Managers')", icon="people")
        +b.tg-unidentified-pane
          loading-wrapper(:loading="isManagersLoading" size="2xl")
            template(v-if="managerList.length")
              +b.tg-unidentified-list
                ds-panel(
                  v-for="manager in managerList"
                  :key="manager.id"
                  space="sm"
                  class="tg-unidentified-list-item-panel"
                ): ds-panel-element(offset="vertical")
                  ds-link(
                    appearance="block"
                    class="tg-unidentified-list-item"
                    :class="{'is-active': selectedManager && selectedManager.id === manager.id, 'is-telegram-disconnected': isManagerTelegramDisconnected(manager)}"
                    @click.prevent="selectManager(manager)"
                  )
                    .tg-unidentified-manager__header
                      ds-caption(variant="strong") {{ getManagerTitle(manager) }}
                      span.tg-unidentified-manager__status(
                        v-if="isManagerTelegramDisconnected(manager)"
                      ) {{ _('Disconnected') }}
                    ds-caption(size="sm") {{ getManagerSubtitle(manager) }}
                    ds-caption(
                      v-if="getManagerTelegramUsername(manager)"
                      size="sm"
                    ) {{ getManagerTelegramUsername(manager) }}
                    ds-caption(
                      v-if="getManagerTelegramPhone(manager)"
                      size="sm"
                    ) {{ getManagerTelegramPhone(manager) }}

            template(v-else)
              ds-panel: ds-panel-element(offset="vertical")
                ds-caption(size="sm") {{ _('No managers with Telegram accounts found.') }}

    //- секция списка неидентифицированных чатов
    //- Для админа зависит от выбранного менеджера
    //- Для менеджера показывает его собственные unidentified-чаты
    cell(
      v-if="shouldShowChatsSection"
      :cols="isAdminView ? ['12', '3-xl'] : ['12', '3-xl']"
      class="tg-unidentified-cell"
    )
      form-fieldset(:caption="_('Chats')", icon="telegram")
        +b.tg-unidentified-pane
          //- даём возврат к списку менеджеров
          +b.tg-unidentified-mobile-actions(v-if="isMobileView && isAdminView")
            control-button.tg-unidentified-back-button(
              styling="primary"
              @click.prevent="goToManagers"
            ) {{ _('Back to managers') }}

            ds-caption(
              v-if="selectedManager"
              size="sm"
            ) {{ getManagerTitle(selectedManager) }}

          loading-wrapper(:loading="isChatsLoading" size="2xl")
            template(v-if="canLoadChats && chatList.length")
              +b.tg-unidentified-list
                ds-panel(
                  v-for="chat in chatList"
                  :key="chat.id"
                  space="sm"
                  class="tg-unidentified-list-item-panel"
                ): ds-panel-element(offset="vertical")
                  +b.tg-unidentified-list-item(
                    :class="{ 'is-active': activeChat && activeChat.id === chat.id }"
                    @click="selectChat(chat)"
                  )
                    +b.tg-unidentified-chat-item__header
                      ds-caption(variant="strong") {{ getChatTitle(chat) }}

                      span(
                        v-if="getChatUnreadCount(chat)"
                        class="tg-unidentified-unread-badge"
                      ) {{ getChatUnreadCount(chat) }}

                    //- Telegram info
                    .tg-unidentified-chat-item__meta
                      ds-caption(size="sm") {{ getChatDetails(chat) }}

                    //- Client / Lead
                    .tg-unidentified-chat-item__meta(
                      v-if="identified && getChatOwner(chat)"
                    )
                      ds-caption(size="sm")
                        span.tg-unidentified-chat-item__meta-label
                          | {{ getChatOwnerTypeTitle(chat) }}:
                        | &nbsp;
                        ds-link(
                          tag="router-link"
                          :to="getChatOwnerRoute(chat)"
                          @click.stop
                        ) {{ getChatOwnerTitle(getChatOwner(chat)) }}

                    //- Last message
                    .tg-unidentified-chat-item__meta(
                      v-if="getChatLastMessageAt(chat)"
                    )
                      ds-caption(size="sm")
                        span.tg-unidentified-chat-item__meta-label {{ _('Last message') }}:
                        |  {{ getChatLastMessageAt(chat) }}

                    +b.tg-unidentified-chat-item__actions(@click.stop)
                      //- create-actions имеют смысл только для unidentified.
                      template(v-if="!identified")
                        control-button(
                          styling="primary"
                          @click.prevent.stop="openIdentifyModal(chat, 'lead')"
                        ) {{ _('Create lead') }}

                        control-button(
                          styling="primary"
                          @click.prevent.stop="openIdentifyModal(chat, 'client')"
                        ) {{ _('Create client') }}

                      //- для уже идентифицированного чата доступны
                      //- существующие административные Relink / Unlink.
                      template(v-else-if="isAdminView")
                        control-button(
                          styling="primary"
                          @click.prevent.stop="openRelinkChatModal(chat)"
                        ) {{ _('Relink chat') }}

                        control-button(
                          styling="primary"
                          @click.prevent.stop="openUnlinkChatModal(chat)"
                        ) {{ _('Unlink chat') }}

            template(v-else-if="!canLoadChats")
              ds-panel: ds-panel-element(offset="vertical")
                ds-caption(size="sm") {{ selectManagerEmptyStateMessage }}

            template(v-else)
              ds-panel: ds-panel-element(offset="vertical")
                ds-caption(size="sm") {{ chatsEmptyStateMessage }}

    //- секция активного чата
    cell(
      v-if="shouldShowMessagesSection"
      :cols="isAdminView ? ['12', '7-xl'] : ['12', '9-xl']"
      class="tg-unidentified-cell"
    )
      form-fieldset(:caption="_('Chat')", icon="chat")
        +b.tg-unidentified-pane
          //- возврат к списку чатов.
          +b.tg-unidentified-mobile-actions(v-if="isMobileView")
            control-button.tg-unidentified-back-button(
              styling="default"
              @click.prevent="goToChats"
            ) {{ _('Back to chats') }}

            ds-caption(
              v-if="activeChat"
              size="sm"
            ) {{ getChatTitle(activeChat) }}

          template(v-if="activeChat")
            //- admin-action для текущего открытого unidentified-чата.
            //- +b.tg-unidentified-chat-item__actions(
            //-   v-if="isAdminView"
            //-   @click.stop
            //- )
            //-   control-button(
            //-     v-if="!identified"
            //-     styling="primary"
            //-     @click.prevent.stop="openRelinkChatModal(activeChat)"
            //-   ) {{ _('Link chat') }}

            //-   template(v-else)
            //-     control-button(
            //-       styling="primary"
            //-       @click.prevent.stop="openRelinkChatModal(activeChat)"
            //-     ) {{ _('Relink chat') }}

            //-     control-button(
            //-       styling="primary"
            //-       @click.prevent.stop="openUnlinkChatModal(activeChat)"
            //-     ) {{ _('Unlink chat') }}
            telegram-chat-conversation(
              :chat="activeChat"
              :title="activeChat ? getChatTitle(activeChat) : ''"
              :subtitle="activeChat ? getChatDetails(activeChat) : ''"
              :can-send-message="canSendMessage"
              :is-refresh-disabled="isChatsLoading"
              :empty-state-message="conversationEmptyStateMessage"
              :messages-empty-state-message="messagesEmptyStateMessage"
              :unread-count="activeChatUnreadCount"
              @seen="handleConversationSeen"
              @refresh-list="receiveChats"
            )

          template(v-else)
            ds-panel: ds-panel-element(offset="vertical")
              ds-caption(size="sm") {{ _('Select a chat to view messages.') }}
</template>

<script>
import { mapState } from 'vuex'
import { hasAccess } from '@aspectus/permissions'
import { toBeSuperuser } from '@md/users/permissions'
import { tgChatApi } from '@md/tg-chat/api'
import { simplifiers, filterAPIMiddleware } from '@md/ui/filter'
import { composeTransformers } from '@md/transformers'
import TelegramChatConversation from '@sections/cabinet/modules/tg-chats/components/TelegramChatConversation'
import TelegramChatIdentifyModal from '@sections/cabinet/modules/tg-chats/components/TelegramChatIdentifyModal'
import TelegramChatRelinkModal from '@sections/cabinet/modules/tg-chats/components/TelegramChatRelinkModal'
import TelegramChatUnlinkConfirmModal from '@sections/cabinet/modules/tg-chats/components/TelegramChatUnlinkConfirmModal'
import { prefixLanguage } from '@md/urls'
import {
  EDIT as EDIT_CLIENT,
} from '@sections/cabinet/modules/clients/views/navigation'
import {
  EDIT as EDIT_LEAD,
} from '@sections/cabinet/modules/leads/views/navigation'

const filtersToAPITransformer = composeTransformers(
  simplifiers.same('profile__type'),
  simplifiers.same('is_active'),
  simplifiers.same('has_telegram_account'),
);
const filterMiddleware = filterAPIMiddleware(filtersToAPITransformer);

export default {
  EDIT_CLIENT,
  EDIT_LEAD,
  props: {
    identified: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSuperuser: false,
      isManagersLoading: false,
      isChatsLoading: false,
      managerList: [],
      selectedManager: null,
      chatList: [],
      activeChat: null,
      // состояние мобильного step-flow
      isMobileView: false,
      mobileStep: 'chats',
      mobileMediaQuery: null,
      mobileMediaQueryListener: null,
    }
  },
  components: {
    TelegramChatConversation,
  },
  computed: {
    ...mapState('users/auth', ['user']),

    isAdminView() {
      return this.isSuperuser
    },

    currentUserInfo() {
      return this.user && this.user.info
        ? this.user.info
        : null
    },

    canLoadChats() {
      if (!this.isAdminView) {
        return true
      }

      return !!(this.selectedManager && this.selectedManager.id)
    },

    // TelegramAccount текущего пользователя
    // Для менеджера отправка идёт через его собственный TelegramAccount
    currentUserTelegramAccount() {
      return this.currentUserInfo && this.currentUserInfo.telegramAccount
        ? this.currentUserInfo.telegramAccount
        : null
    },

    // аккаунт, через который разрешена отправка
    // Для админа используем TelegramAccount выбранного менеджера чата
    sendingTelegramAccount() {
      if (
        this.isAdminView
        && this.activeChat
        && this.activeChat.manager
        && this.activeChat.manager.telegramAccount
      ) {
        return this.activeChat.manager.telegramAccount
      }

      if (
        this.isAdminView
        && this.selectedManager
        && this.selectedManager.telegramAccount
      ) {
        return this.selectedManager.telegramAccount
      }

      return this.currentUserTelegramAccount
    },

    // отправка доступна только если выбран чат
    // и есть TelegramAccount, через который можно отправлять
    canSendMessage() {
      return !!(
        this.activeChat
        && this.activeChat.id
        && this.sendingTelegramAccount
      )
    },
    conversationEmptyStateMessage() {
      return this._('Select a chat to view messages.')
    },
    messagesEmptyStateMessage() {
      if (!this.activeChat) {
        return this.conversationEmptyStateMessage
      }

      return this._('There are no messages in this chat yet.')
    },
    activeChatUnreadCount() {
      if (!this.activeChat) {
        return 0
      }

      return this.getChatUnreadCount(this.activeChat)
    },

    shouldShowManagersSection() {
      return !!(
        this.isAdminView
        && (
          !this.isMobileView
          || this.mobileStep === 'managers'
        )
      )
    },

    shouldShowChatsSection() {
      return !!(
        !this.isMobileView
        || this.mobileStep === 'chats'
      )
    },

    shouldShowMessagesSection() {
      return !!(
        !this.isMobileView
        || this.mobileStep === 'messages'
      )
    },

    selectManagerEmptyStateMessage() {
      return this.identified
        ? this._('Select a manager to see identified chats.')
        : this._('Select a manager to see unidentified chats.')
    },

    chatsEmptyStateMessage() {
      return this.identified
        ? this._('No identified Telegram chats found.')
        : this._('No unidentified Telegram chats found.')
    },
  
  },
  // при переключении Unidentified / Identified Vue может переиспользовать
  // тот же экземпляр компонента, поэтому mounted повторно не вызывается.
  // Явно сбрасываем выбранный чат и перечитываем список с новым identified.
  watch: {
    identified: {
      async handler(newValue, oldValue) {
        if (newValue === oldValue) {
          return
        }

        this.activeChat = null
        this.chatList = []

        if (this.isMobileView) {
          this.mobileStep = this.isAdminView
            ? 'managers'
            : 'chats'
        }

        await this.receiveChats()
      },
    },
  },
  async mounted() {
    this.initMobileView()

    await this.setAccessSuperuser([toBeSuperuser])
    await this.ensureCurrentUserLoaded()

    this.setInitialMobileStep()

    if (this.isAdminView) {
      await this.receiveManagers()
      return
    }

    await this.receiveChats()
  },
  beforeDestroy() {
    this.destroyMobileView()
  },
  methods: {
    async setAccessSuperuser(permissions) {
      await hasAccess(permissions || [], this)
        .then(() => {
          this.isSuperuser = true
        })
        .catch(() => {
          this.isSuperuser = false
        })
    },

    async ensureCurrentUserLoaded() {
      if (!(this.user && this.user.determined)) {
        try {
          await this.$store.dispatch('users/auth/initialReceive')
        } catch (error) {
          // Ошибку не пробрасываем, чтобы страница чатов не падала целиком
        }
      }
    },

    openUnlinkChatModal(chat) {
      if (
        !this.isAdminView
        || !(chat && chat.id)
      ) {
        return
      }

      this.$modal.show(
        TelegramChatUnlinkConfirmModal,
        {
          promise: () => this.unlinkChat(chat),
        },
        {
          height: 'auto',
          adaptive: true,
          scrollable: true,
        }
      )
    },

    async unlinkChat(chat) {
      await tgChatApi.chat.unlink.execute(
        {
          chatId: chat.id,
        },
        {}
      )

      this.$toast.success(
        this._('Telegram chat successfully unlinked.')
      )

      if (
        this.activeChat
        && this.activeChat.id === chat.id
      ) {
        this.activeChat = null
      }

      await this.receiveChats()
    },

    extractListItems(response) {
      // TODO оставить один, фактический
      if (Array.isArray(response)) {
        return response
      }
      if (Array.isArray(response?.data?.items)) {
        return response.data.items
      }
      if (Array.isArray(response?.items)) {
        return response.items
      }
      if (Array.isArray(response?.results)) {
        return response.results
      }

      return []
    },

    async receiveManagers() {
      console.log('receiveManagers')
      this.isManagersLoading = true

      try {
        // Нужны только сотрудники с verified TelegramAccount
        // TODO и это должны быть только менеджеры! (пока что)
        const parameters = {
          filters: {
            is_active: true,
            has_telegram_account: true,
            profile__type: ['0001000-manager',]
          },
          limit: 99,
        };
        console.log('parameters', parameters)
        const response = await this.$api.staff.list.middleware(filterMiddleware).execute(
          parameters
        )
        console.log('response', response)

        this.managerList = this.extractListItems(response)

        if (this.managerList.length) {
          this.selectedManager = this.managerList[0]
          await this.receiveChats()
        }
      } catch (error) {
        this.managerList = []
        this.selectedManager = null
        this.chatList = []
        this.activeChat = null
      } finally {
        this.isManagersLoading = false
      }
    },

    async selectManager(manager) {
      this.selectedManager = manager
      this.activeChat = null
      if (this.isMobileView) {
        this.goToChats()
      }
      await this.receiveChats()
    },

    async receiveChats() {
      if (!this.canLoadChats) {
        this.chatList = []
        this.activeChat = null
        return
      }

      this.isChatsLoading = true

      try {
        // один и тот же компонент обслуживает оба таба.
        // Backend уже поддерживает identified=0/1 вместе с managerId.
        const params = {
          identified: this.identified ? 1 : 0,
        }

        if (this.isAdminView && this.selectedManager && this.selectedManager.id) {
          params.managerId = this.selectedManager.id
        }

        const response = await tgChatApi.list.execute(params)
        this.chatList = this.extractListItems(response)

        if (
          this.activeChat
          && !this.chatList.some(chat => chat.id === this.activeChat.id)
        ) {
          this.activeChat = null
        }
      } catch (error) {
        this.chatList = []
        this.activeChat = null
      } finally {
        this.isChatsLoading = false
      }

      if (this.activeChat) {
        this.activeChat = this.chatList.find(
          chat => chat.id === this.activeChat.id
        ) || this.activeChat
      }
    },

    selectChat(chat) {
      this.activeChat = chat
      if (this.isMobileView) {
        this.goToMessages()
      }
    },

    getManagerTitle(manager) {
      return (
        manager?.displayName
        || manager?.profile?.displayName
        || manager?.email
        || manager?.username
        || this._('Manager')
      )
    },

    getManagerSubtitle(manager) {
      return (
        manager?.objId
        || manager?.profile?.objId
        || manager?.email
        || ''
      )
    },

    getManagerTelegramAccount(manager) {
      return (
        manager?.telegramAccount
        || manager?.telegram_account
        || null
      );
    },

    getManagerTelegramUsername(manager) {
      const account = this.getManagerTelegramAccount(manager);
      const username = account?.username || '';

      if (!username) {
        return '';
      }

      return username.startsWith('@')
        ? username
        : `@${username}`;
    },

    getManagerTelegramPhone(manager) {
      const account = this.getManagerTelegramAccount(manager);

      return account?.phone || '';
    },

    getChatLastMessageAt(chat) {
      const value = (
        chat?.lastMessageAt
        || chat?.last_message_at
      );

      if (!value) {
        return '';
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return new Intl.DateTimeFormat(
        undefined,
        {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }
      ).format(date);
    },

    getChatOwner(chat) {
      return (
        chat?.client
        || chat?.lead
        || null
      )
    },

    getChatOwnerTypeTitle(chat) {
      if (chat?.client) {
        return this._('Client')
      }

      if (chat?.lead) {
        return this._('Lead')
      }

      return ''
    },

    getChatOwnerRoute(chat) {
      if (chat?.client?.id) {
        return {
          name: this.$options.EDIT_CLIENT,
          params: {
            id: chat.client.id,
          },
        }
      }

      if (chat?.lead?.id) {
        return {
          name: this.$options.EDIT_LEAD,
          params: {
            id: chat.lead.id,
          },
        }
      }

      return null
    },

    getChatOwnerTitle(owner) {
      if (!owner) {
        return ''
      }

      const fullName = [
        owner.name,
        owner.middleName || owner.middle_name,
        owner.surname,
      ].filter(Boolean).join(' ')

      return (
        owner.displayName
        || owner.display_name
        || owner.title
        || fullName
        || owner.objId
        || owner.obj_id
        || owner.email
        || `#${owner.id}`
      )
    },

    // красное состояние показываем только если backend
    // явно сообщил isVerified=false, а не при отсутствии поля.
    isManagerTelegramDisconnected(manager) {
      const account = this.getManagerTelegramAccount(manager);

      if (!account) {
        return false;
      }

      return (
        account.isVerified === false
        || account.is_verified === false
      );
    },

    getChatTitle(chat) {
      const fullName = [
        chat?.firstName,
        chat?.lastName,
      ].filter(Boolean).join(' ')

      return (
        fullName
        || chat?.username
        || this._('Telegram user')
      )
    },

    getChatTelegramId(chat) {
      if (!chat?.tgUserId) {
        return ''
      }

      return `${this._('Telegram ID')}: ${chat.tgUserId}`
    },

    getChatUsername(chat) {
      if (!chat?.username) {
        return ''
      }

      return `@${chat.username}`
    },

    getChatPhone(chat) {
      if (!chat?.phone) {
        return ''
      }

      return chat.phone
    },

    getChatDetails(chat) {
      return [
        this.getChatTelegramId(chat),
        this.getChatUsername(chat),
        this.getChatPhone(chat),
      ].filter(Boolean).join(' • ')
    },

    getChatUnreadCount(chat) {
      const value = Number(chat?.tgUnreadCount || chat?.unreadCount || 0)

      if (!Number.isFinite(value) || value < 0) {
        return 0
      }

      return value
    },

    // conversation-компонент сообщает
    // что сообщения активного чата были открыты/прочитаны
    // Parent локально обнуляет unread в списке unidentified-чатов
    handleConversationSeen() {
      this.markActiveChatUnreadLocallyAsSeen()
    },

    // локально обнуляем unread у выбранного unidentified-чата
    // чтобы бейдж исчез без полной перезагрузки страницы
    markActiveChatUnreadLocallyAsSeen() {
      if (!(this.activeChat && this.activeChat.id)) {
        return
      }

      this.chatList = this.chatList.map(chat => {
        if (chat.id !== this.activeChat.id) {
          return chat
        }

        return {
          ...chat,
          tgUnreadCount: 0,
          unreadCount: 0,
        }
      })

      this.activeChat = this.chatList.find(
        chat => chat.id === this.activeChat.id
      ) || this.activeChat
    },

        // >>> ADDED
    // ADDED: открываем модалку создания/привязки лида или клиента.
    // Attach работает сразу через LinkClientAPIView.
    // Create редиректит на существующую страницу создания с query из чата.
    openIdentifyModal(chat, mode) {
      const pr = arg => new Promise((resolve, reject) => {
        if (arg) {
          resolve(arg)
          return
        }

        reject(arg)
      }).then(() => {
        this.activeChat = null
        this.receiveChats()
      })

      this.$modal.show(
        TelegramChatIdentifyModal,
        {
          chat,
          mode,
          createRoute: this.getCreateRouteFromChat(chat, mode),
          promise: pr,
        },
        {
          height: 'auto',
          adaptive: true,
          scrollable: true,
        }
      )
    },

    // привязка unidentified-чата к существующему Client/Lead.
    // Используем тот же admin link endpoint, что и для перепривязки identified-чата.
    openRelinkChatModal(chat) {
      if (
        !this.isAdminView
        || !(chat && chat.id)
      ) {
        return
      }

      const pr = arg => new Promise((resolve, reject) => {
        if (arg) {
          resolve(arg)
          return
        }

        reject(arg)
      }).then(() => {
        this.activeChat = null
        this.receiveChats()
      })

      this.$modal.show(
        TelegramChatRelinkModal,
        {
          chat,
          promise: pr,
        },
        {
          height: 'auto',
          adaptive: true,
          scrollable: true,
        }
      )
    },

    // готовим route на существующие страницы создания
    // Данные передаём через query, чтобы они переживали обычный path-based переход
    getCreateRouteFromChat(chat, mode) {
      return {
        path: mode === 'lead'
          ? prefixLanguage('/cabinet/leads/create/')
          : prefixLanguage('/cabinet/clients/create/'),
        query: this.getCreateQueryFromChat(chat),
      }
    },

    // query-параметры для предзаполнения формы создания лида/клиента
    getCreateQueryFromChat(chat) {
      return {
        tg_chat_source: 'unidentified',
        tg_chat_id: chat?.id || '',
        tg_user_id: chat?.tgUserId || chat?.tg_user_id || '',
        tg_username: chat?.username || '',
        tg_phone: chat?.phone || '',
        tg_first_name: chat?.firstName || chat?.first_name || '',
        tg_last_name: chat?.lastName || chat?.last_name || '',
        tg_manager_id: chat?.manager?.id || this.selectedManager?.id || '',
        tg_manager_title:
          chat?.manager?.displayName
          || chat?.manager?.display_name
          || chat?.manager?.email
          || '',
      }
    },
    // включаем mobile step-flow на ширинах ниже xl
    initMobileView() {
      if (typeof window === 'undefined' || !window.matchMedia) {
        return
      }

      this.mobileMediaQuery = window.matchMedia('(max-width: 1199px)')
      this.isMobileView = this.mobileMediaQuery.matches

      const listener = event => {
        this.isMobileView = event.matches
        this.setInitialMobileStep()
      }

      this.mobileMediaQueryListener = listener

      if (typeof this.mobileMediaQuery.addEventListener === 'function') {
        this.mobileMediaQuery.addEventListener('change', listener)
        return
      }

      this.mobileMediaQuery.addListener(listener)
    },

    destroyMobileView() {
      if (!this.mobileMediaQuery || !this.mobileMediaQueryListener) {
        return
      }

      if (typeof this.mobileMediaQuery.removeEventListener === 'function') {
        this.mobileMediaQuery.removeEventListener('change', this.mobileMediaQueryListener)
        return
      }

      this.mobileMediaQuery.removeListener(this.mobileMediaQueryListener)
    },

    setInitialMobileStep() {
      if (!this.isMobileView) {
        return
      }

      this.mobileStep = this.isAdminView
        ? 'managers'
        : 'chats'
    },

    goToManagers() {
      if (!this.isAdminView) {
        return
      }
      this.mobileStep = 'managers'
    },

    goToChats() {
      this.mobileStep = 'chats'
    },

    goToMessages() {
      this.mobileStep = 'messages'
    },
  },
}
</script>
